import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, ChevronRight } from 'lucide-react';

import ParticleBackground from '../components/ui/ParticleBackground';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, type: 'spring', stiffness: 100 } }
  };

  // Subtle tech grid
  const TechGrid = () => (
    <div className="absolute inset-0 z-0">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-gray-300 h-full" />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-gray-300 w-full" />
        ))}
      </div>
    </div>
  );

  // Soft scanline
  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/10 to-transparent h-32"
      animate={{ top: ['-10%', '110%'] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
    />
  );

  const lastUpdated = "March 2, 2026";
  const effectiveDate = "March 2, 2026";

  const sections = [
    {
      id: "introduction",
      title: "1. Introduction",
      content: `Reticuli Technologies, LLC ("Company," "we," "us," or "our") respects your privacy. This Privacy Policy explains how we handle information when you visit our website or contact us.

We do not currently collect customer personal data at scale. If and when we do, we will update this policy to reflect our specific practices.`
    },
    {
      id: "information-collected",
      title: "2. Information We Collect",
      content: `Right now, the main information we collect is what you choose to send us through our contact form: your name, email address, and the contents of your message.

We may also automatically collect basic, non-identifying technical information (such as browser type) through standard website hosting and analytics tools.`
    },
    {
      id: "use-of-information",
      title: "3. How We Use Your Information",
      content: `We use the information you submit through our contact form only to respond to your inquiry. We do not sell your information, and we do not share it with third parties except as needed to operate our contact form (such as our form processing provider) or if required by law.`
    },
    {
      id: "data-security",
      title: "4. Data Security",
      content: `We take reasonable steps to protect the limited information we currently collect. As our security practices mature, more detail will be published on our Security page.`
    },
    {
      id: "your-rights",
      title: "5. Your Privacy Rights",
      content: `You can ask us what information we have about you, or ask us to delete it, by emailing us at the address in Section 7.`
    },
    {
      id: "updates",
      title: "6. Updates to This Privacy Policy",
      content: `We'll update the "Last Updated" date above if we make changes to this policy, particularly once we begin collecting customer data at scale.`
    },
    {
      id: "contact",
      title: "7. Contact Us",
      content: `If you have questions about this Privacy Policy, please contact us:

**Reticuli Technologies, LLC**

**Email**: privacy@reticulitech.com

**Mailing Address**:
Reticuli Technologies, LLC
351 St Francis St
Mobile, AL 36602
United States`
    }
  ];

  return (
    <div className="min-h-screen bg-black text-gray-100">
      {/* Hero Section */}
      <section className="relative min-h-[45vh] flex items-center justify-center overflow-hidden bg-black">
        <ParticleBackground />
        <TechGrid />
        <ScanLine />

        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />

        <div className="container mx-auto px-4 z-20 relative pt-32 pb-20">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gray-700/50 bg-gray-900/50">
                <Shield className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
              </div>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-extralight tracking-wider text-white mb-3"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Privacy Policy
            </motion.h1>

            <motion.p variants={itemVariants} className="text-gray-400 tracking-widest text-sm mb-4">
              RETICULI TECHNOLOGIES
            </motion.p>

            <motion.div variants={itemVariants} className="flex items-center justify-center gap-6 text-sm">
              <p className="text-gray-500">
                Last Updated: {lastUpdated}
              </p>
              <div className="w-1 h-1 rounded-full bg-gray-600" />
              <p className="text-gray-500">
                Effective: {effectiveDate}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="relative py-12 bg-gradient-to-b from-black via-gray-950 to-gray-950 border-b border-gray-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gray-900/40 border border-gray-800/50 rounded-sm p-8"
            >
              <h2 className="text-lg font-medium text-white mb-6 tracking-wider">Table of Contents</h2>
              <nav className="grid md:grid-cols-2 gap-2">
                {sections.map((section, index) => (
                  <motion.a
                    key={section.id}
                    href={`#${section.id}`}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.03 }}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200 py-1 flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 bg-gray-600 group-hover:bg-white transition-colors duration-200" />
                    {section.title}
                  </motion.a>
                ))}
              </nav>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="relative py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, type: 'spring', stiffness: 100 }}
                className="mb-16 scroll-mt-24"
              >
                <h2 
                  className="text-xl md:text-2xl font-light text-white mb-6 tracking-wider pb-3 border-b border-gray-800/50"
                  style={{ fontFamily: 'Georgia, Cambria, serif' }}
                >
                  {section.title}
                </h2>
                <div className="prose prose-invert prose-gray max-w-none">
                  {section.content.split('\n\n').map((paragraph, pIndex) => {
                    // Check if paragraph is a header (starts with **)
                    if (paragraph.startsWith('**') && paragraph.includes('**\n')) {
                      const [header, ...rest] = paragraph.split('\n');
                      return (
                        <motion.div 
                          key={pIndex} 
                          className="mb-4"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: pIndex * 0.05 }}
                        >
                          <h3 className="text-base font-medium text-gray-200 mb-2">
                            {header.replace(/\*\*/g, '')}
                          </h3>
                          <p className="text-gray-400 leading-relaxed text-sm whitespace-pre-line">
                            {rest.join('\n')}
                          </p>
                        </motion.div>
                      );
                    }
                    
                    // Check if paragraph contains bullet points
                    if (paragraph.includes('• ')) {
                      const lines = paragraph.split('\n');
                      return (
                        <div key={pIndex} className="mb-4">
                          {lines.map((line, lIndex) => {
                            if (line.startsWith('• ')) {
                              return (
                                <motion.div 
                                  key={lIndex} 
                                  className="flex items-start gap-3 mb-2"
                                  initial={{ opacity: 0, x: -10 }}
                                  whileInView={{ opacity: 1, x: 0 }}
                                  viewport={{ once: true }}
                                  transition={{ delay: lIndex * 0.03 }}
                                >
                                  <span className="text-gray-600 mt-1.5">•</span>
                                  <span 
                                    className="text-gray-400 text-sm leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                      __html: line.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-300 font-medium">$1</strong>')
                                    }}
                                  />
                                </motion.div>
                              );
                            }
                            return (
                              <p 
                                key={lIndex} 
                                className="text-gray-400 text-sm leading-relaxed mb-2"
                                dangerouslySetInnerHTML={{
                                  __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-300 font-medium">$1</strong>')
                                }}
                              />
                            );
                          })}
                        </div>
                      );
                    }
                    
                    // Regular paragraph
                    return (
                      <motion.p 
                        key={pIndex} 
                        className="text-gray-400 leading-relaxed text-sm mb-4"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: pIndex * 0.05 }}
                        dangerouslySetInnerHTML={{
                          __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-300 font-medium">$1</strong>')
                        }}
                      />
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative py-20 bg-gradient-to-b from-gray-950 to-black border-t border-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-gray-500 mb-8 text-lg">
              Questions about our privacy practices?
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-medium tracking-wider rounded-lg transition-all duration-300 group text-sm border border-gray-600/30"
              >
                Contact Us
                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={16} />
              </Link>
              <Link
                to="/terms"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-700 hover:border-gray-600 text-gray-400 hover:text-white font-medium tracking-wider rounded-lg transition-all duration-300 text-sm"
              >
                Terms of Service
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;